/**
 * Plain date string utilities to avoid timezone issues
 * 
 * Key principle: Keep YYYY-MM-DD strings in state and APIs for day-only values.
 * Only convert to Date objects for formatting/display, not for storage.
 * 
 * The UTC shift pitfall: new Date('2025-10-16') creates a date at midnight UTC,
 * which may shift to the previous day in local timezones (e.g., PST/PDT).
 * Use parseLocalDate() for local dates and dateFromYMD_UTCNoon() for consistent UTC.
 */

/**
 * Parse a strict YYYY-MM-DD string as a local date (no timezone shift)
 * 
 * This is the primary function for parsing date-only values stored as YYYY-MM-DD strings.
 * It creates a Date object using local timezone components, avoiding the UTC shift
 * pitfall where new Date('2025-10-16') creates midnight UTC (previous day in PST/PDT).
 * 
 * Use this when:
 * - You have a strict YYYY-MM-DD format string
 * - You need day-only date values (no time component)
 * - You want to avoid timezone shifts
 * 
 * For flexible parsing of multiple formats, use parseFlexibleLocalDate() from chartUtils.
 * 
 * @param s YYYY-MM-DD string (strict format required)
 * @returns Date object in local timezone, or null if invalid
 * 
 * @example
 * parseLocalDate('2025-10-16') // Date for Oct 16, 2025 (local, no TZ shift)
 * parseLocalDate('2025-13-45') // null (invalid date)
 */
export const parseLocalDate = (s: string): Date | null => {
  if (!s || typeof s !== 'string') return null;
  const [y, m, d] = s.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  
  // Validate date components
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > 31) return null;
  if (y < 1900 || y > 2100) return null;
  
  // Create date and check if it's valid (handles month/day overflow)
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  
  return date;
};

/**
 * Format a Date object as YYYY-MM-DD string
 * @param d Date object
 * @returns YYYY-MM-DD string
 */
export const formatYMD = (d: Date): string => {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0')
  ].join('-');
};

// Removed unused export in Phase 4 cleanup: dateFromYMD_UTCNoon
// (function body removed - was unused)

/**
 * Get current date as YYYY-MM-DD string
 * @returns Current date as YYYY-MM-DD string
 */
export const todayYMD = (): string => {
  return formatYMD(new Date());
};

/**
 * Check if a string is a valid YYYY-MM-DD date
 * @param s String to check
 * @returns true if valid YYYY-MM-DD format
 */
// Removed unused export in Phase 4 cleanup: isValidYMD
// (function body removed - was unused)

export const isValidYMD = (s: string): boolean => {
  const ymdRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!ymdRegex.test(s)) return false;
  
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  
  return date.getFullYear() === y && 
         date.getMonth() === m - 1 && 
         date.getDate() === d;
};
